import { useEffect, useState } from 'react';
import { ITicker } from '@kattana-io/common';
import Text from '../../../atoms/Text';
import {
  IMobileTableScema,
  formatMobileTableData,
} from '../../../helpers/formatters';
import PairIcons from '../../../molecules/PairIcons';
import useTicker from '../../../hooks/useTicker';
import { switchCurrentTicker } from '../../../Authlogic';

import TableCardMobile, {
  contentColors,
} from '../../../molecules/Table/mobile/TableCard';
import NumberDisplay from '../../../molecules/NumberDisplay';
import {
  formatBaseVolume,
  formatUSD,
  getPrice,
} from '../../../components/Modal/TickersModal/helpers';
import { Loader } from '../../../components/DexTrades';
import Clock from '../Clock';
import { getPriceChange } from '../../../helpers/priceChange';
import { IRates } from '../../../reducers/rates';
import TableContractField from '../../../components/TableComponents/Contract';
import Slider from '../../../molecules/Slider';

interface IBigTradesTableMobileProps {
  data: ITicker[];
  inputSearch: string;
  loading: boolean;
  rates: IRates;
  page: string;
  onSetPage(page: number): void;
}

const BigTradesTableMobile = ({
  data,
  inputSearch,
  loading,
  rates,
  page,
  onSetPage,
}: IBigTradesTableMobileProps) => {
  const [tickers, setTickers] = useState<ITicker[]>(data);

  useEffect(() => {
    setTickers(
      data.filter(
        (ticker) =>
          ticker.name.toLowerCase().includes(inputSearch.toLowerCase()) ||
          ticker.address.toLowerCase().includes(inputSearch.toLowerCase())
      )
    );
  }, [data, inputSearch]);

  const schema: IMobileTableScema = {
    row_1: {
      item_1: {
        name: 'Pair',
        accessor: 'pair',
        parser: (_, row: ITicker) => {
          const ticker = useTicker(row);
          return (
            <div className='ticker__cell'>
              <PairIcons
                chain={ticker.chain}
                pair={ticker.name}
                base_address={ticker.base_address}
                quote_address={ticker.quote_address}
                base_slug={ticker.base_slug}
                quote_slug={ticker.quote_slug}
              />
              <Text size={14} className='text--weight-500 text--grey-400'>
                {ticker.name}
              </Text>
            </div>
          );
        },
        onClick: (row) => switchCurrentTicker(row.ticker),
      },
      item_2: {
        name: 'Token price USD',
        accessor: 'price_a_usd',
        parser: (_, row) => {
          const price = getPrice(row, rates);
          const { priceChangePercent } = getPriceChange(price.rawPrice, row);
          return (
            <div className='new_pairs_mobile_price_change'>
              <NumberDisplay
                value={price.rawPrice}
                startSymbol={price.sign}
                textProps={{ size: 14 }}
              />
              {!!priceChangePercent && (
                <NumberDisplay
                  value={priceChangePercent}
                  round={2}
                  startSymbol={priceChangePercent > 0 ? '+' : ''}
                  endSymbol='%'
                  textProps={{ size: 14 }}
                />
              )}
            </div>
          );
        },
        string: true,
      },
    },
    row_2: {
      item_1: {
        name: 'Listed since',
        accessor: 'pool_created',
        parser: (value) => <Clock timestamp={value * 1000} />,
      },
      item_2: {
        name: 'Total liquidity',
        accessor: 'liquidity',
        parser: (value) => formatUSD(value),
      },
    },
    row_3: {
      item_1: {
        name: '24H Volume',
        accessor: 'volume',
        parser: (value) => formatBaseVolume(value),
      },
      item_2: {
        name: 'Pool Base',
        accessor: 'base_reserve',
        parser: (_, row: ITicker) => {
          const ticker = useTicker(row);
          return (
            <NumberDisplay
              value={ticker.base_reserve}
              round={0}
              endSymbol={` ${ticker.base}`}
              string
            />
          );
        },
        string: true,
      },
    },
    row_4: {
      item_1: {
        name: 'Pool quote',
        accessor: 'quote_reserve',
        parser: (_, row: ITicker) => {
          const ticker = useTicker(row);
          return (
            <NumberDisplay
              value={ticker.quote_reserve}
              round={0}
              endSymbol={` ${ticker.quote}`}
              string
            />
          );
        },
        string: true,
      },
      item_2: {
        name: 'Contract',
        accessor: 'address',
        parser: (value, row) => (
          <TableContractField address={value} chain={row.chain} />
        ),
      },
    },
  };

  const getColorForItem = (row: ITicker): contentColors => {
    const price = getPrice(row, rates);
    const { priceChangePercent } = getPriceChange(price.rawPrice, row);

    if (priceChangePercent > 0) {
      return 'green';
    }
    if (priceChangePercent < 0) {
      return 'red';
    }
    return 'grey';
  };

  return (
    <>
      {loading ? (
        <div className='centrify'>
          <Loader />
        </div>
      ) : !tickers.length ? (
        <div className='centrify'>
          <Text size={16} className='text--grey-400'>
            No data
          </Text>
        </div>
      ) : (
        <>
          {formatMobileTableData({
            data: tickers,
            schema,
            getColorForItem,
            spaceBetween: true,
          }).map((row, index) => (
            <TableCardMobile key={index} data={{ ...row, opened: true }} />
          ))}
          <Slider
            page={Number(page)}
            isFinal={data?.length ? data.length % 100 !== 0 : true}
            setPage={onSetPage}
          />
        </>
      )}
    </>
  );
};

export default BigTradesTableMobile;
